package ui.campaign

import androidx.compose.animation.*
import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.*
import androidx.compose.foundation.shape.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.*
import androidx.compose.ui.draw.*
import androidx.compose.ui.graphics.vector.*
import androidx.compose.ui.platform.*
import androidx.compose.ui.text.font.*
import androidx.compose.ui.text.style.*
import androidx.compose.ui.unit.*
import api.*
import common.frontend.api.*
import compose.assets.*
import compose.assets.svg.*
import kotlinx.coroutines.*
import org.jetbrains.compose.resources.*
import provider.*
import ssoft.frontend.generated.resources.*
import tools.*
import ui.*
import ui.widget.*

private class ListingFilterScope(
    private val campaignScope: CampaignListingScope,
    private val drawerState: DrawerState,
    private val coroutine: CoroutineScope,
    val display: CommonDisplayProvider = campaignScope.display
) {
    private val default = Io.Filter.CampaignListing()
    private var last = campaignScope.vm.filter

    var filter by mutableStateOf(campaignScope.vm.filter)
        private set

    fun change(newFilter: Io.Filter.CampaignListing) {
        canReloadToDefault = newFilter != default
        canApplyChanges = last != newFilter
        filter = newFilter
    }

    var canApplyChanges by mutableStateOf(false)
        private set

    var canReloadToDefault by mutableStateOf(false)
        private set

    val locations = campaignScope.vmGlobal.companyWithLocation
        ?.items
        ?.firstOrNull { it.id == campaignScope.args.companyId }
        ?.location

    companion object {
        @Composable
        fun remember(
            origin: CampaignListingScope,
            drawerState: DrawerState
        ): ListingFilterScope {
            val coroutine = rememberCoroutineScope()
            return remember {
                ListingFilterScope(origin, drawerState, coroutine)
            }
        }
    }

    fun applyChanges(close: Boolean) {
        canApplyChanges = false

        if (campaignScope.vm.filter != filter) {
            campaignScope.vm.filter = filter
            last = filter
            campaignScope.action(CampaignListingVM.Action.Reload)
        }
        canReloadToDefault = last != default
        if (close) {
            coroutine.launch {
                drawerState.close()
            }
        }
    }

    fun resetToDefault() {
        filter = Io.Filter.CampaignListing()
        applyChanges(false)
    }
}

@Composable
fun CampaignListingScope.CampaignListingFilter(
    state: DrawerState = rememberDrawerState(initialValue = DrawerValue.Closed),
    body: @Composable () -> Unit
) {

    with(ListingFilterScope.remember(this, state)) {
        if (display.resolution.width > 1800) {
            Row(Modifier.fillMaxSize()) {
                Box(Modifier.weight(1F).fillMaxHeight()) {
                    body()
                }
                ModalDrawerSheet(
                    drawerShape = RoundedCornerShape(0, 0, 0, 0)
                ) {
                    Content()
                }
            }
        } else {
            CompositionLocalProvider(LocalLayoutDirection provides LayoutDirection.Rtl) {
                DismissibleNavigationDrawer(
                    drawerState = state,
                    gesturesEnabled = true,
                    content = {
                        CompositionLocalProvider(LocalLayoutDirection provides LayoutDirection.Ltr) {
                            body()
                        }
                    },
                    drawerContent = {
                        CompositionLocalProvider(LocalLayoutDirection provides LayoutDirection.Ltr) {
                            ModalDrawerSheet(
                                drawerShape = RoundedCornerShape(topStartPercent = 5, bottomStartPercent = 5)
                            ) {
                                Content()
                            }
                        }
                    })
            }
        }
    }
}

@Composable
private fun ListingFilterScope.Content() {
    Column(
        Modifier.width(360.dp)
            .fillMaxHeight()
    ) {
        LazyColumn(
            modifier = Modifier.fillMaxWidth()
                .weight(1F),
            contentPadding = PaddingValues(horizontal = 8.dp, vertical = 16.dp)
        ) {
            item {
                Text(
                    text = Res.string.layer_top_listing__filter_title.rememberString(),
                    textAlign = TextAlign.Center,
                    style = MaterialTheme.typography.displayMedium,
                    modifier = Modifier.fillMaxWidth()
                )
            }

            item {
                Dates()
            }

            item {
                States()
            }

            item {
                Modes()
            }
            Locations(this@Content)
        }
        Actions()
        Space(16.dp)
    }
}

private fun LazyListScope.Locations(scope: ListingFilterScope) {
    with(scope) {
        locations?.also { locations ->
            item {
                Space(32.dp)
                Row {
                    Icon(
                        imageVector = Svg.Map_marker_multiple,
                        contentDescription = null
                    )
                    Space(8.dp)
                    Text(
                        text = Res.string.layer_top_listing__filter_locations_title.rememberString(),
                        style = MaterialTheme.typography.titleLarge,
                        modifier = Modifier.align(Alignment.CenterVertically)
                    )
                }
            }
            items(locations) {
                LocationItem(it) {
                    if (it.id in filter.excludedLocations) {
                        change(
                            filter.copy(
                                excludedLocations = filter.excludedLocations - it.id
                            )
                        )
                    } else {
                        change(
                            filter.copy(
                                excludedLocations = filter.excludedLocations + it.id
                            )
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun RowScope.SmallActionPlus(enabled: Boolean, onClick: () -> Unit) {
    SmallAction(Svg.Plus, enabled, onClick)
}

@Composable
private fun RowScope.SmallActionMinus(enabled: Boolean, onClick: () -> Unit) {
    SmallAction(Svg.Minus_thick, enabled, onClick)
}

@Composable
private fun RowScope.SmallAction(icon: ImageVector, enabled: Boolean, onClick: () -> Unit) {
    val fg by animateColorAsState(
        if (enabled) MaterialTheme.colorScheme.onSecondary
        else MaterialTheme.colorScheme.onSurface
    )
    val bg by animateColorAsState(
        if (enabled) MaterialTheme.colorScheme.secondary
        else MaterialTheme.colorScheme.surface
    )
    Icon(imageVector = icon,
        contentDescription = null,
        tint = fg,
        modifier = Modifier.align(Alignment.CenterVertically)
            .background(bg, CircleShape)
            .clip(CircleShape)
            .clickable(enabled) { onClick() }
            .padding(2.dp)
            .size(16.dp))
}

@Composable
private fun ListingFilterScope.LocationItem(it: Io.BundleCompanyWithLocation.Location, onClick: () -> Unit) {
    InputChip(
        modifier = Modifier.fillMaxWidth(),
        selected = it.id !in filter.excludedLocations,
        onClick = onClick,
        label = { Text(it.name, maxLines = 1, overflow = TextOverflow.Ellipsis) }
    )
}


@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun ListingFilterScope.States() {
    Space(32.dp)
    Row {
        Icon(
            imageVector = Svg.State_machine,
            contentDescription = null
        )
        Space(8.dp)
        Text(
            text = Res.string.layer_top_listing__filter_state_title.rememberString(),
            style = MaterialTheme.typography.titleLarge,
            modifier = Modifier.align(Alignment.CenterVertically)
        )
    }
    Space(4.dp)
    FlowRow(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        CampaignState.all.forEach {
            StateItem(it) {
                if (it match filter.states) {
                    change(filter.copy(states = filter.states - it.state))
                } else {
                    change(filter.copy(states = filter.states + it.state))
                }
            }
        }
    }
}

@Composable
private fun ListingFilterScope.StateItem(it: CampaignState, onClick: () -> Unit) {
    InputChip(
        selected = it match filter.states,
        label = { Text(it.labelRes().rememberString()) },
        onClick = onClick
    )
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun ListingFilterScope.Modes() {
    Space(32.dp)
    Row {
        Icon(
            imageVector = Svg.Shape,
            contentDescription = null
        )
        Space(8.dp)
        Text(
            text = Res.string.layer_top_listing__filter_mode_title.rememberString(),
            style = MaterialTheme.typography.titleLarge,
            modifier = Modifier.align(Alignment.CenterVertically)
        )
    }
    Space(4.dp)
    FlowRow(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        CampaignMode.all.forEach {
            ModeItem(it) {
                if (it match filter.mode) {
                    change(filter.copy(mode = filter.mode - it.value))
                } else {
                    change(filter.copy(mode = filter.mode + it.value))
                }
            }
        }
    }
}


@Composable
private fun ListingFilterScope.Dates() {
    DateItem(
        label = Res.string.layer_top_listing__filter_date,
        field = { filter.dtRange },
        onChange = {
            change(filter.copy(dtRange = it))
        })
}


@Composable
private fun ListingFilterScope.DateItem(
    label: StringResource,
    field: () -> Io.Filter.DateRange?,
    onChange: (Io.Filter.DateRange?) -> Unit
) {
    Space(32.dp)
    val state = DialogState.remember()
    DateRangePickerDialog(state) { start, end ->
        onChange(
            Io.Filter.DateRange(
                start = start,
                end = end
            )
        )
    }
    Row {
        Icon(
            imageVector = Svg.Calendar,
            contentDescription = null
        )
        Space(8.dp)
        Text(
            text = label.rememberString(),
            style = MaterialTheme.typography.titleLarge,
            modifier = Modifier.align(Alignment.CenterVertically)
        )
        Space(8.dp)
        AnimatedContent(
            targetState = field() != null,
            modifier = Modifier.align(Alignment.CenterVertically)
        ) { isSet ->
            if (isSet) {
                SmallActionMinus(true) {
                    onChange(null)
                }
            } else {
                SmallActionPlus(true) {
                    state.show()
                }
            }
        }
    }
    AnimatedContent(field()) { entry ->
        entry?.also {
            Column {
                Space(4.dp)
                InputChip(true,
                    label = {
                        Column(modifier = Modifier.padding(vertical = 4.dp)) {
                            Row {
                                Text(
                                    text = Res.string.layer_top_listing__filter_dt_inline_from.rememberString(),
                                    fontWeight = FontWeight.Thin,
                                    modifier = Modifier.weight(1F)
                                )
                                Text(
                                    text = it.start.formatFromRes(),
                                    fontWeight = FontWeight.Normal,
                                    modifier = Modifier.weight(2F)
                                )
                            }
                            Row {
                                Text(
                                    text = Res.string.layer_top_listing__filter_dt_inline_to.rememberString(),
                                    fontWeight = FontWeight.Thin,
                                    modifier = Modifier.weight(1F)
                                )
                                Text(
                                    text = it.end.formatFromRes(),
                                    fontWeight = FontWeight.Normal,
                                    modifier = Modifier.weight(2F)
                                )
                            }
                        }

                    },
                    onClick = { state.show() })
            }
        }
    }
}

@Composable
private fun ListingFilterScope.ModeItem(it: CampaignMode, onClick: () -> Unit) {
    InputChip(
        selected = it match filter.mode,
        label = { Text(it.label.rememberString()) },
        onClick = onClick
    )
}


@Composable
private fun ListingFilterScope.Actions() {
    val scope = rememberCoroutineScope()


    fun apply(close: Boolean) {
        scope.launch {
            when {
                filter.states == 0 -> {
                    snackBarStateGlobal.showSnackbar(
                        message = Res.string.layer_top_listing__filter_state_warn.await(),
                        duration = SnackbarDuration.Short,
                        withDismissAction = true
                    )
                }

                filter.mode == 0 -> {
                    snackBarStateGlobal.showSnackbar(
                        message = Res.string.layer_top_listing__filter_mode_warn.await(),
                        duration = SnackbarDuration.Short,
                        withDismissAction = true
                    )
                }

                filter.excludedLocations.containsAll(locations?.map { it.id } ?: listOf()) -> {
                    snackBarStateGlobal.showSnackbar(
                        message = Res.string.layer_top_listing__filter_locations_warn.await(),
                        duration = SnackbarDuration.Short,
                        withDismissAction = true
                    )
                }

                else -> {
                    applyChanges(close)
                }
            }
        }
    }
    Row {

        Spacer(Modifier.weight(1F))
        IconButton(
            enabled = canReloadToDefault,
            onClick = { resetToDefault() },
            colors = IconButtonDefaults.iconButtonColors(
                containerColor = MaterialTheme.colorScheme.secondary,
                contentColor = MaterialTheme.colorScheme.onSecondary,
            )
        ) {
            Icon(
                imageVector = Svg.Refresh,
                contentDescription = null
            )
        }

        Spacer(Modifier.weight(1F))
        IconButton(
            enabled = canApplyChanges || display.resolution.width < 1800,
            onClick = { apply(true) },
            colors = IconButtonDefaults.iconButtonColors(
                containerColor = MaterialTheme.colorScheme.primary,
                contentColor = MaterialTheme.colorScheme.onPrimary,
            )
        ) {
            Icon(
                imageVector = Svg.Check,
                contentDescription = null
            )
        }
        Spacer(Modifier.weight(1F))
    }
}

private sealed class CampaignMode(val label: StringResource, val value: Int) {
    data object Commercial : CampaignMode(Res.string.layer_top_listing__filter_mode_commercial, Io.Filter.CampaignListing.MODE_C)
    data object NonCommercial : CampaignMode(Res.string.layer_top_listing__filter_mode_non_commercial, Io.Filter.CampaignListing.MODE_NC)
    data object NonAndCommercial : CampaignMode(Res.string.layer_top_listing__filter_mode_non_and_commercial, Io.Filter.CampaignListing.MODE_C_NC)

    infix fun match(state: Int): Boolean = this.value and state > 0


    companion object {
        val all by lazy {
            listOf(Commercial, NonCommercial, NonAndCommercial)
        }
    }
}

